import { useMutation, useQueryClient } from "@tanstack/react-query";

import { addFieldCropPractice, addFieldCropPracticeAdmin, addFieldCropPracticeSimulations } from "services";

import { getEnvironment, invalidateCarbonProgramQueriesByChunk, invalidateQueriesByChunk } from "utils";

import {
  ADD_FIELD_CROP_PRACTICE,
  GET_FARM_DATA_SUBMISSION_STATUS,
  GET_FIELDS_CROP_RECOMMENDATIONS,
  GET_FIELD_PRACTICES,
  GET_FIELD_PRACTICE_FILTER_OPTIONS,
  GET_INCENTIVES_POTENTIAL_PAYOUTS,
} from "consts";

import { CropPracticesRequestBody } from "models";

type MutateParams = {
  fieldId: string;
  simulationId?: string;
  requestBody: CropPracticesRequestBody;
};

export const useAddFieldCropPractice = ({ fieldId, farmId, simulationId }: { fieldId: string; farmId?: string; simulationId?: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ADD_FIELD_CROP_PRACTICE, fieldId, simulationId],
    mutationFn: ({ requestBody }: { requestBody: MutateParams["requestBody"] }) => mutate({ fieldId, simulationId, requestBody }),
    onSuccess: () => {
      invalidateCarbonProgramQueriesByChunk(fieldId, simulationId);
      queryClient.invalidateQueries({ queryKey: [GET_INCENTIVES_POTENTIAL_PAYOUTS] });
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICE_FILTER_OPTIONS] });
      if (!simulationId) {
        invalidateQueriesByChunk(GET_FIELD_PRACTICES, fieldId);
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]] });
        queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farmId] });
      } else {
        queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICES, fieldId, simulationId] });
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId], [simulationId]] });
      }
    },
  });
};

const mutate = ({ fieldId, simulationId, requestBody }: MutateParams) => {
  const isAdmin = getEnvironment().admin;

  if (isAdmin) {
    return addFieldCropPracticeAdmin({ fieldId, requestBody });
  }

  if (simulationId) {
    return addFieldCropPracticeSimulations({ fieldId, simulationId, requestBody });
  }

  return addFieldCropPractice({ fieldId, requestBody });
};
